/**
 * Shared CSV formula-injection guard for every CSV serializer in the admin UI.
 *
 * Spreadsheet apps (Excel, Google Sheets) interpret a cell as the start of a
 * formula when its first character is one of a handful of "trigger" characters.
 * Prefixing such a cell with a single quote neutralizes that without changing
 * how a human reads the value.
 *
 * This predicate is the single source of truth shared by both the generic
 * DataTable serializer (`csv-export.ts`) and the Explore serializer
 * (`features/datasets/utils/exportCsv.ts`) so the two cannot drift.
 */

/**
 * Returns true when a string cell would be interpreted as a formula by a
 * spreadsheet app and therefore needs a leading single quote.
 *
 * `=` `@` and the control chars tab/CR are always risky; `+`/`-` are only risky
 * when the value is not a plain number, so legitimate negatives (e.g. "-5") and
 * signed numbers (e.g. "+1234") are left intact.
 */
export function needsFormulaGuard(s: string): boolean {
  const first = s[0];
  if (first === '=' || first === '@' || first === '\t' || first === '\r') return true;
  if (first === '+' || first === '-') {
    // A live payload like "-2+3+cmd()" is not a finite number; a real "-5" is.
    return !Number.isFinite(Number(s));
  }
  return false;
}
